/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { memo, useEffect, useRef, useState } from "react";
import type { FunctionComponent, JSX, ReactNode } from "react";
import { ImageUrlGenerator, type TChannel } from "@tivocorp/data-domain-lib";
import { RecordingStatusV2 } from "@tivocorp/data-domain-lib";
import { FeedItemType } from "client-code/smart-search";

import { Log } from "../../../model/logging";
import { OfferType, CarouselTemplate } from "../../../model/enums";
import type { TileProps } from "./TileProps";
import { useAccessibilityFocusSync } from "../../hooks/useAccessibilityFocusSync";
import { CarouselTemplateMapper } from "../../../viewModel/utility/CarouselTemplateUtil";
import "./Tile.scss";
import { ChannelsInteractor } from "../../../model/interactor";
import {
  selectChannelLineupRevision,
  selectIsTvDevice,
  useUnitySelector
} from "../../../viewModel";
import { TileBadges } from "../TileBadges";
import { selectRecordingsStatusMap } from "../../../viewModel/recordingsStatus";

const calculateLiveProgress = (
  startTime?: string,
  endTime?: string
): number | null => {
  if (!startTime || !endTime) return null;
  const start = Date.parse(startTime);
  const end = Date.parse(endTime);
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
  const now = Date.now();
  const progress = ((now - start) / (end - start)) * 100;
  return Math.min(100, Math.max(0, progress));
};

interface TResolvedChannelLogo {
  callSign: string;
  logoUrl: string;
}

/**
 * Last call sign and logo URL each station resolved to, kept across mounts.
 * The logo image itself is already in the browser cache like the poster, but
 * the lookup that finds its URL is async — without this, every remount (e.g.
 * returning to Home) draws the call sign first and swaps to the logo a moment
 * later. A remounted tile reapplies the last answer at once; the lookup still
 * runs and overwrites it, so a changed line-up is picked up.
 */
const resolvedChannelLogos: Map<string, TResolvedChannelLogo> = new Map();

interface TSpokenChannel {
  channelName: string;
  channelNumber: string;
}

/**
 * VoiceView: channel name and number each station resolved to, kept across
 * mounts so a remounted tile can speak them at once (same reasoning as
 * resolvedChannelLogos).
 */
const spokenChannels: Map<string, TSpokenChannel> = new Map();

/**
 * VoiceView: resolves the channel name and number the visible TilePreview
 * shows for an item that only carries a stationId, so the spoken label
 * matches it. Runs on mount (not on focus) so the answer is normally in
 * before the user reaches the tile. Pass undefined to skip.
 */
const useSpokenChannel = (
  stationId: string | undefined
): TSpokenChannel | undefined => {
  const [channel, setChannel] = useState<TSpokenChannel | undefined>(
    stationId === undefined ? undefined : spokenChannels.get(stationId)
  );
  useEffect((): (() => void) | void => {
    if (stationId === undefined) {
      return;
    }
    let isCurrent: boolean = true;
    new ChannelsInteractor()
      .getChannelByStationId(stationId)
      .then((resolved: TChannel | null): void => {
        if (!isCurrent || resolved === null) {
          return;
        }
        const name: string =
          resolved.name !== undefined && resolved.name !== ""
            ? resolved.name
            : resolved.callSign || "";
        const spoken: TSpokenChannel = {
          channelName: name,
          channelNumber: resolved.channelNumber || ""
        };
        spokenChannels.set(stationId, spoken);
        setChannel(spoken);
      })
      .catch((error: unknown): void => {
        Log.e("Tile", "useSpokenChannel — getChannelByStationId()", error);
      });
    return (): void => {
      isCurrent = false;
    };
  }, [stationId]);
  return channel;
};

/**
 * The station a channel tile stands for. A LinearProvider tile carries no
 * stationId of its own — its id is the station.
 */
const resolveStationId = (
  feedItem: TileProps["feedItem"]
): string | undefined => {
  if (feedItem.stationId !== undefined && feedItem.stationId !== "") {
    return feedItem.stationId;
  }
  if (feedItem.feedItemType === FeedItemType.LinearProvider) {
    return feedItem.id;
  }
  return undefined;
};

/** VoiceView: "<rail title>, <spoken preview>" or just the spoken preview. */
const buildTileAccessibilityLabel = (
  feedItem: TileProps["feedItem"],
  spokenChannel: TSpokenChannel | undefined,
  getAccessibilityLabel: TileProps["getAccessibilityLabel"],
  prefix: string | undefined
): string => {
  const item: TileProps["feedItem"] =
    spokenChannel === undefined
      ? feedItem
      : {
          ...feedItem,
          channelName: spokenChannel.channelName,
          channelNumber:
            spokenChannel.channelNumber !== ""
              ? spokenChannel.channelNumber
              : feedItem.channelNumber
        };
  const label: string =
    getAccessibilityLabel !== undefined
      ? getAccessibilityLabel(item)
      : item.title;
  return prefix !== undefined && prefix !== "" ? prefix + ", " + label : label;
};

const TileComponent: FunctionComponent<TileProps> = (
  props: TileProps
): JSX.Element => {
  const {
    feedItem,
    template,
    isFocused,
    isGhostHovered,
    onFocus,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onKeyDown,
    isChannelThumbnail,
    showChannelLogo,
    renderPreview,
    isAccessibilityFocused,
    getAccessibilityLabel,
    accessibilityLabelPrefix
  } = props;

  // VoiceView (opt-in per rail): real DOM focus follows the D-pad focus.
  const tileRef = useRef<HTMLDivElement>(null);
  useAccessibilityFocusSync(tileRef, isAccessibilityFocused === true);

  const imageUrl: string = feedItem.imageUrl;
  const title: string = feedItem.title;

  // VoiceView: the tile is named by its spoken preview (or its title), with
  // the rail title in front on the first tile focused in a newly entered rail.
  const isAccessibilityManaged: boolean = isAccessibilityFocused !== undefined;
  const spokenChannel: TSpokenChannel | undefined = useSpokenChannel(
    isAccessibilityManaged &&
      getAccessibilityLabel !== undefined &&
      (feedItem.channelName === undefined || feedItem.channelName === "")
      ? feedItem.stationId
      : undefined
  );
  const accessibilityLabel: string | undefined = isAccessibilityManaged
    ? buildTileAccessibilityLabel(
        feedItem,
        spokenChannel,
        getAccessibilityLabel,
        accessibilityLabelPrefix
      )
    : undefined;

  // Track which URL had an error, not just whether there was an error
  // This allows the tile to retry loading when imageUrl changes
  const [erroredUrl, setErroredUrl] = useState<string | null>(null);
  const imageError = erroredUrl === imageUrl;

  const TAG: string = "Tile";
  const stationId: string | undefined =
    isChannelThumbnail || showChannelLogo
      ? resolveStationId(feedItem)
      : undefined;
  const cachedLogo: TResolvedChannelLogo | undefined =
    stationId === undefined ? undefined : resolvedChannelLogos.get(stationId);
  const [resolvedCallSign, setResolvedCallSign] = useState<string>(
    cachedLogo !== undefined && cachedLogo.callSign !== ""
      ? cachedLogo.callSign
      : title
  );
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState<string>(
    cachedLogo === undefined ? "" : cachedLogo.logoUrl
  );
  // Kept apart from `erroredUrl`: that one tracks the poster, and a logo that
  // failed to load must not hide it — nor stay hidden once the logo URL changes.
  const [erroredLogoUrl, setErroredLogoUrl] = useState<string | null>(null);
  const logoError = erroredLogoUrl === resolvedLogoUrl;

  const recordingStatusMap = useUnitySelector(selectRecordingsStatusMap);
  const isSmartTV = useUnitySelector(selectIsTvDevice);
  // A profile switch empties the channel cache before refilling it, so a tile
  // that mounts in between finds no channel. Re-resolve once it is refilled.
  const channelLineupRevision = useUnitySelector(selectChannelLineupRevision);

  const offerId = feedItem.offerId;
  const hasValidOfferId = offerId !== undefined && offerId !== "";
  const recordingStatusEntry = hasValidOfferId
    ? recordingStatusMap[offerId]
    : undefined;

  const resolvedRecordingStatus =
    recordingStatusEntry !== undefined && recordingStatusEntry !== null
      ? recordingStatusEntry.recordingStatus
      : undefined;

  useEffect((): (() => void) | void => {
    if (stationId === undefined) {
      return;
    }

    let isCurrent: boolean = true;
    new ChannelsInteractor()
      .getChannelByStationId(stationId)
      .then((channel: TChannel | null): void => {
        if (!isCurrent || channel === null) {
          return;
        }
        const cached: TResolvedChannelLogo | undefined =
          resolvedChannelLogos.get(stationId);
        const resolved: TResolvedChannelLogo = {
          callSign: cached === undefined ? "" : cached.callSign,
          logoUrl: cached === undefined ? "" : cached.logoUrl
        };
        if (channel.callSign !== undefined && channel.callSign !== "") {
          resolved.callSign = channel.callSign;
          setResolvedCallSign(channel.callSign);
        }
        if (
          channel.logoIndex !== undefined &&
          channel.channelLogoUrl !== undefined &&
          channel.channelLogoUrl !== ""
        ) {
          const baseUrl: string = channel.channelLogoUrl.split("/logos/")[0];
          const imageUrlGenerator: ImageUrlGenerator = new ImageUrlGenerator();
          resolved.logoUrl =
            imageUrlGenerator.buildImageUrlForHomeScreenChannelThumbnail(
              baseUrl,
              channel.logoIndex
            );
          setResolvedLogoUrl(resolved.logoUrl);
        }
        resolvedChannelLogos.set(stationId, resolved);
      })
      .catch((error: unknown): void => {
        Log.e(TAG, "useEffect — getChannelByStationId()", error);
      });

    return (): void => {
      isCurrent = false;
    };
  }, [stationId, channelLineupRevision]);

  const handleImageError = (): void => {
    setErroredUrl(imageUrl);
  };

  const renderFallbackImage = (isHidden: boolean = false): JSX.Element => (
    <div
      className={"tile__fallback" + (isHidden ? " tile__poster--hidden" : "")}
    >
      {title !== undefined && title !== "" && (
        <span className="tile__fallback-title">{title}</span>
      )}
    </div>
  );

  const handleLogoError = (): void => {
    setErroredLogoUrl(resolvedLogoUrl);
  };

  const renderChannelLogo = (): JSX.Element => {
    if (!resolvedLogoUrl || logoError) {
      return (
        <div className="tile-channel-logo-wrapper">
          <span className="tile-channel-logo-fallback">{resolvedCallSign}</span>
        </div>
      );
    }
    return (
      <div className="tile-channel-logo-wrapper">
        <img
          className="tile-channel-logo"
          src={resolvedLogoUrl}
          alt={resolvedCallSign}
          onError={handleLogoError}
        />
      </div>
    );
  };

  const renderPoster = (isHidden: boolean): JSX.Element => {
    if (!imageUrl || imageError) {
      return renderFallbackImage(isHidden);
    }
    return (
      <img
        className={"tile__image" + (isHidden ? " tile__poster--hidden" : "")}
        src={imageUrl}
        alt={title}
        loading="lazy"
        onError={handleImageError}
      />
    );
  };

  const renderImage = (): ReactNode => {
    if (showChannelLogo) {
      return renderChannelLogo();
    }
    // Channel thumbnails keep the poster mounted (and so loaded) like any
    // other rail, with the logo laid over it until the tile takes focus —
    // focusing then reveals an already-loaded poster instead of starting a fetch.
    if (isChannelThumbnail && !isFocused) {
      return (
        <>
          {renderPoster(true)}
          {renderChannelLogo()}
        </>
      );
    }
    return renderPoster(false);
  };

  const baseClass: string = "tile";
  const className: string =
    baseClass +
    " " +
    CarouselTemplateMapper[template] +
    (isSmartTV ? " " + baseClass + "--smart-tv" : "") +
    (isFocused ? " " + baseClass + "--focused" : "") +
    (isGhostHovered === true ? " " + baseClass + "--ghost-hovered" : "");

  const renderProgress = (): JSX.Element | null => {
    let computed: number | null = 0;
    if (feedItem.offerType === OfferType.Live) {
      computed = calculateLiveProgress(feedItem.startTime, feedItem.endTime);
      if (computed === null || computed <= 0) return null;
    } else {
      computed =
        feedItem.progressPercent !== undefined ? feedItem.progressPercent : 0;
      if (computed <= 0) return null;
    }
    return (
      <div className="tile__progress">
        <div className="tile__progress-inactive" />
        <div
          className="tile__progress-active"
          style={{ width: String(computed) + "%" }}
        />
      </div>
    );
  };

  /**
   * The artwork badge strip, now drawn by the shared {@link TileBadges} — the
   * same renderer My Hub's landscape cards use, so a tile and a card show the
   * same badges. Recording is the one flag this surface derives rather than
   * reads: an item the recordings map says is recording right now counts, not
   * only one the feed flagged.
   */
  const renderBadges = (): JSX.Element | null => (
    <TileBadges
      isRecording={
        resolvedRecordingStatus === RecordingStatusV2.InProgressRecording ||
        feedItem.isRecording === true
      }
      isLive={feedItem.isLive}
      isUpcoming={feedItem.isUpcoming}
      isNew={feedItem.isNew}
      isSaved={feedItem.isSaved}
      isWatched={feedItem.isWatched}
    />
  );

  const renderTileInfo = (): ReactNode => {
    if (renderPreview !== undefined) {
      return renderPreview(isFocused);
    }
    return <div className="tile__title">{title}</div>;
  };

  const shouldShowTileDecorations = !isChannelThumbnail || isFocused;

  return (
    <div
      ref={tileRef}
      className={className}
      tabIndex={0}
      role="button"
      aria-label={accessibilityLabel}
      onFocus={onFocus}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={onKeyDown}
    >
      <div className="tile__image-container">
        {renderImage()}
        {shouldShowTileDecorations && renderBadges()}
        {shouldShowTileDecorations &&
          template !== CarouselTemplate.POSTER_HORIZONTAL &&
          renderProgress()}
      </div>
      <div className="tile__info">{renderTileInfo()}</div>
    </div>
  );
};

/**
 * Memoized Tile component to prevent unnecessary re-renders during
 * carousel navigation. Uses default shallow comparison on all props
 * to ensure callback updates (onFocus, onClick) are never missed.
 */
const Tile = memo(TileComponent);

export default Tile;
